import { DOCUMENT } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { Router, NavigationEnd, ActivatedRoute } from '@angular/router';
import { filter, map } from 'rxjs/operators';
import { merge } from 'rxjs';
import { BreadcrumbService } from './breadcrumb.service';

const DEFAULT_META_DESCRIPTION =
  'Elementrix is a data catalog and governance platform for discovering data products, managing domains, business terms, and collaborating across your organization.';

/**
 * Service to manage dynamic page titles across the application.
 * Extracts titles from route data and updates document.title accordingly.
 *
 * Guidelines followed:
 * - Single Responsibility: Handles only title management
 * - Maintainability: Centralized title logic for easy updates
 * - Error Handling: Gracefully handles missing titles with fallbacks
 */
@Injectable({
  providedIn: 'root',
})
export class TitleService {
  private readonly _router = inject(Router);
  private readonly _title = inject(Title);
  private readonly _meta = inject(Meta);
  private readonly _document = inject(DOCUMENT);
  private readonly _breadcrumbService = inject(BreadcrumbService);
  private readonly _defaultTitle = 'Elementrix';

  /**
   * Initialize title service to listen to route changes and breadcrumb updates
   * Should be called once in app component or shell component
   *
   * Guidelines followed:
   * - Reactive Programming: Listens to both route changes and dynamic breadcrumb updates
   * - Maintainability: Single subscription handles all title update triggers
   */
  initialize(): void {
    // Listen to route navigation events
    const routeEvents$ = this._router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map(() => this._router.routerState.root)
    );

    // Listen to dynamic breadcrumb updates (for dynamic titles like product names, domain names)
    const breadcrumbUpdates$ = this._breadcrumbService.dynamicUpdate$;

    // Merge both streams to update title on either route change or breadcrumb update
    merge(routeEvents$, breadcrumbUpdates$).subscribe(() => {
      this.updateTitle();
    });

    // Initial title update
    this.updateTitle();
  }

  /**
   * Update document title based on current route and dynamic content
   * Traverses the route tree to find the deepest route with a title and section
   * Prioritizes dynamic titles (from breadcrumb service) over static route titles
   */
  private updateTitle(): void {
    const { title, section } = this.getTitleAndSectionFromRoute(
      this._router.routerState.root
    );

    // Check if breadcrumb service has dynamic title updates (e.g., product names, domain names)
    const dynamicTitle = this.getDynamicTitle();

    // If we have a dynamic title, use it as the primary title
    // Otherwise, use the route title
    const primaryTitle = dynamicTitle || title;

    if (primaryTitle) {
      // Clean title - remove section prefix if it's already in the title
      let cleanTitle = primaryTitle;
      if (section && primaryTitle.startsWith(`${section} - `)) {
        // Remove section prefix from title if it's already there
        cleanTitle = primaryTitle.replace(`${section} - `, '');
      }

      // Build title parts: section (if available) + clean title
      const titleParts: string[] = [];

      if (section) {
        titleParts.push(section);
      }

      titleParts.push(cleanTitle);

      const finalTitle = titleParts.join(' - ');
      const documentTitle = `${this._defaultTitle} - ${finalTitle}`;
      this._title.setTitle(documentTitle);
      this.syncPageMetadata(documentTitle, finalTitle);
    } else {
      this._title.setTitle(this._defaultTitle);
      this.syncPageMetadata(this._defaultTitle, '');
    }
  }

  /**
   * Aligns meta description, Open Graph, Twitter cards, and canonical URL with the document title.
   * Improves Lighthouse SEO and social previews for SPA navigations.
   */
  private syncPageMetadata(documentTitle: string, subtitleForDescription: string): void {
    const descriptionSource =
      subtitleForDescription.trim() || DEFAULT_META_DESCRIPTION;
    const description =
      descriptionSource.length > 160
        ? `${descriptionSource.slice(0, 157)}...`
        : descriptionSource;

    this._meta.updateTag({ name: 'description', content: description });
    this._meta.updateTag({ property: 'og:title', content: documentTitle });
    this._meta.updateTag({ property: 'og:description', content: description });
    this._meta.updateTag({ property: 'og:type', content: 'website' });
    this._meta.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
    this._meta.updateTag({ name: 'twitter:title', content: documentTitle });
    this._meta.updateTag({ name: 'twitter:description', content: description });

    const origin = this._document.location?.origin;
    if (origin) {
      const path = this._router.url.split('#')[0];
      const href = `${origin}${path}`;
      this._meta.updateTag({ property: 'og:url', content: href });
      let link = this._document.querySelector(
        'link[rel="canonical"]',
      ) as HTMLLinkElement | null;
      if (!link) {
        link = this._document.createElement('link');
        link.setAttribute('rel', 'canonical');
        this._document.head.appendChild(link);
      }
      link.setAttribute('href', href);
    }
  }

  /**
   * Recursively get title and section from route and its children
   * Returns the title and section from the deepest activated route
   * Checks both route.title and route.data.title for compatibility
   */
  private getTitleAndSectionFromRoute(route: ActivatedRoute): {
    title: string | null;
    section: string | null;
  } {
    let title: string | null = null;
    let section: string | null = null;

    // Traverse to the deepest child route
    let child = route;
    while (child.firstChild) {
      child = child.firstChild;
    }

    // Get title from deepest route - check both route.title and route.data.title
    if (!title) {
      title = (child.snapshot.routeConfig?.title as string) || null;
    }
    if (!title && child.snapshot.data['title']) {
      title = child.snapshot.data['title'];
    }

    // Get section from deepest route data
    if (child.snapshot.data['section']) {
      section = child.snapshot.data['section'];
    }

    // If no title or section found in deepest route, check parent routes
    if (!title || !section) {
      let currentRoute: ActivatedRoute | null = route;
      while (currentRoute && (!title || !section)) {
        // Check route.title first, then route.data.title
        if (!title) {
          title = (currentRoute.snapshot.routeConfig?.title as string) || null;
        }
        if (!title && currentRoute.snapshot.data['title']) {
          title = currentRoute.snapshot.data['title'];
        }

        // Check section from route data
        if (!section && currentRoute.snapshot.data['section']) {
          section = currentRoute.snapshot.data['section'];
        }
        currentRoute = currentRoute.parent;
      }
    }

    return { title, section };
  }

  /**
   * Get dynamic title from breadcrumb service
   * Used for pages with dynamic content (e.g., product names, term names, domain names)
   */
  private getDynamicTitle(): string | null {
    // Check for common dynamic breadcrumb keys
    const productName =
      this._breadcrumbService.getDynamicBreadcrumb('productName');
    const termName = this._breadcrumbService.getDynamicBreadcrumb('termName');
    const categoryName =
      this._breadcrumbService.getDynamicBreadcrumb('categoryName');
    const domainName =
      this._breadcrumbService.getDynamicBreadcrumb('currentBreadcrumb');

    return productName || termName || categoryName || domainName || null;
  }

  /**
   * Manually set title (useful for components that need custom titles)
   * @param title - The title to set
   * @param section - Optional section name to include in the title
   */
  setTitle(title: string, section?: string): void {
    const titleParts: string[] = [];

    if (section) {
      titleParts.push(section);
    }

    titleParts.push(title);

    const finalTitle = titleParts.join(' - ');
    const documentTitle = `${this._defaultTitle} - ${finalTitle}`;
    this._title.setTitle(documentTitle);
    this.syncPageMetadata(documentTitle, finalTitle);
  }

  /**
   * Get the default application title
   */
  getDefaultTitle(): string {
    return this._defaultTitle;
  }
}
